import { useNavigate } from "@remix-run/react";
import Cookies from "js-cookie";
import { useEffect, useRef } from "react";
import MobileDetect from 'mobile-detect';

const CRAWLER_USER_AGENT_PATTERN =
  /(googlebot|google-inspectiontool|googleother|storebot-google|adsbot-google|mediapartners-google|apis-google|feedfetcher-google|google-extended|bingbot|slurp|duckduckbot|baiduspider|yandexbot|applebot|facebookexternalhit|twitterbot|linkedinbot|petalbot)/i;

export function isCrawlerUserAgent(userAgent: string) {
  return CRAWLER_USER_AGENT_PATTERN.test(userAgent);
}

/** PageSpeed / Lighthouse / headless lab runners (not Googlebot). */
const PERF_LAB_USER_AGENT_PATTERN =
  /(Chrome-Lighthouse|Lighthouse|PageSpeed|PTST|HeadlessChrome|GTmetrix)/i;

export function isPerformanceLabUserAgent(userAgent: string) {
  return PERF_LAB_USER_AGENT_PATTERN.test(userAgent);
}

/** True in PSI / automated Lighthouse; DevTools Lighthouse may not set these. */
export function isPerformanceLabEnvironment() {
  if (typeof navigator === "undefined") return false;
  if (isPerformanceLabUserAgent(navigator.userAgent)) return true;
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver) return true;
  return false;
}

export function useViewportListener(breakpoint = 768) {
  const lastType = useRef<string | null>(null);
  useEffect(() => {
    const md = new MobileDetect(window.navigator.userAgent);
    const updateViewportCookies = () => {
      const width = window.innerWidth;
      const type = ((width < breakpoint) || md.mobile()) ? "mobile" : "desktop";

      // Store width and type in cookies
      Cookies.set("screenWidth", width.toString(), { path: "/" });
      Cookies.set("loadedComponent", type, { path: "/" });

      // Initialize the ref if it's the first run
      if (lastType.current === null) {
        lastType.current = type;
        return;
      }

      // Only reload if type actually changed
      if (lastType.current !== type) {
        lastType.current = type;

        // Force a full reload to switch layout correctly
        window.location.reload();
      }
    };

    // Run immediately on mount
    updateViewportCookies();

    // Debounce resize to avoid jitter reloads
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateViewportCookies, 300);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [breakpoint]);
}

const parseCookie = (str: string) =>
  str
    .split(';')
    .map((v: string) => v.split('='))
    .reduce((acc: { [x: string]: string; }, v: string[]) => {
      acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1]?.trim());
      return acc;
    }, {});

export function getDeviceInfo(request: Request, breakpoint = 1024) {
  const cookie = request.headers.get("cookie") || "";
  const cookies = parseCookie(cookie);

  let screenWidth = parseInt(cookies.screenWidth || "0", 10);
  const userAgent = request.headers.get('user-agent') || '';
  const isCrawler = isCrawlerUserAgent(userAgent);

  // Keep bots on the stable SSR layout path so mobile-only client logic
  // does not turn an otherwise valid page into a crawler soft-404.
  if (isCrawler) {
    return {
      screenWidth: screenWidth > 0 ? screenWidth : 1200,
      isMobile: false,
      isParentPage: false,
      isCrawler: true,
    };
  }

  // If we don't have a screenWidth cookie, use user-agent to guess mobile
  const md = new MobileDetect(userAgent);
  if (!cookies.screenWidth) {
    // If it's a mobile device, we set screenWidth to 375 (typical mobile width) to trigger mobile layout
    if (md.mobile()) {
      screenWidth = 375;
    } else {
      screenWidth = 1200; // typical desktop width
    }
  }

  const isMobile = md.mobile() !== null || (screenWidth > 0 && screenWidth < breakpoint);
  const isParentPage = screenWidth > 0 && screenWidth < 768;

  return { screenWidth, isMobile, isParentPage, isCrawler: false };
}